import { routeShape } from "../components/RouteMap/routeLegs";
import { routeMapFingerprint } from "./routeMapPoints";
import type { TruckReport } from "../types/containerReports";

/**
 * ── Measuring the truck report where the server could not ────────────────────
 *
 * The report's kilometres are measured on the server, which falls back to
 * straight lines between the stops unless it has a Maps key that works
 * server-side (`GOOGLE_MAPS_SERVER_KEY`). The browser reading the report *does*
 * have a working key — it is the one the container's own page measures roads
 * with — so where the server could only estimate, the figures are measured
 * again here, over the very same points, and the two pages stop disagreeing
 * about the same container.
 *
 * ── What this deliberately does not do ───────────────────────────────────────
 *
 * It does not measure what the server already measured properly. A row whose
 * own `kmSource` is `road` is the server's answer, cached in the database
 * against the stops it was measured over and shared by every viewer;
 * re-measuring it in a browser would be slower, cost a Directions call per
 * route, and could only ever disagree.
 *
 * That decision is made **per row**, off `kmSource`, and never off the
 * report-wide `distanceSource` — a distinction that cost a whole round of this
 * bug. The report-wide field used to answer "what is this deployment
 * configured to attempt", not "what did it manage": with a key present but
 * rejected, it said `road` while every row had fallen back to a straight line,
 * so a pass guarded on it measured nothing at all and the two pages went on
 * disagreeing. The rows know what they are; the configuration does not.
 *
 * And it does not write anything back. What a browser measures stays in that
 * browser, so this costs a Directions call per *distinct route* per report
 * load — routes repeat heavily across a batch, and `routeShape` caches them, so
 * a truck's week of identical runs is one call. That is the trade this makes:
 * agreement now, paid for on every load, rather than measured once server-side.
 *
 * ── Why it happens before the report is shown ───────────────────────────────
 *
 * The totals and the bars are sums. Letting measurements land one by one would
 * have every figure on the page climb while it is being read — so the caller
 * awaits this and shows its existing loading state until it is done.
 */

/** Matching the server's own fan-out in `resolveRouteDistances`. */
const CONCURRENCY = 4;

const round1 = (km: number) => Math.round(km * 10) / 10;

/** Rows the server already measured on roads are left exactly as they are. */
const needsMeasuring = (line: TruckReport["trucks"][number]["lines"][number]) =>
  line.kmSource !== "road" && !!line.points && line.points.length >= 2;

export async function withRoadDistances(report: TruckReport): Promise<TruckReport> {
  /** Every distinct run still worth measuring, by fingerprint. */
  const routes = new Map<string, { lat: number; lng: number }[]>();
  for (const truck of report.trucks) {
    for (const line of truck.lines) {
      // Under two located stops there is nothing to measure, and the server's
      // own figure (0, or null) already says so.
      if (!needsMeasuring(line)) continue;
      routes.set(routeMapFingerprint(line.points), line.points);
    }
  }
  if (!routes.size) return report;

  const measured = new Map<string, { km: number; source: "road" | "straight" }>();
  const queue = [...routes.entries()];

  const worker = async () => {
    for (;;) {
      const next = queue.shift();
      if (!next) return;
      const [key, points] = next;
      // Never rejects: a refused Directions call comes back as the
      // straight-line estimate, labelled as one.
      const shape = await routeShape(points);
      if (shape.legs.length) {
        measured.set(key, { km: shape.totalKm, source: shape.source });
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker),
  );

  if (!measured.size) return report;

  // One estimate among them keeps the whole report labelled an estimate: the
  // totals are sums, and a sum containing one straight-line leg is not a road
  // distance however many of its parts were measured on roads.
  let allRoad = true;

  const trucks = report.trucks.map((truck) => {
    let km = 0;

    const lines = truck.lines.map((line) => {
      const answer = needsMeasuring(line)
        ? measured.get(routeMapFingerprint(line.points))
        : undefined;

      if (!answer) {
        km += line.km ?? 0;
        // A row left as it was drags the label down only if it is a real
        // distance that is not a road one — an unmeasurable run (no located
        // stops, or only one) is neither, and must not make the whole report
        // read as an estimate.
        if (line.kmSource !== "road" && (line.km ?? 0) > 0) allRoad = false;
        return line;
      }

      if (answer.source !== "road") allRoad = false;
      km += answer.km;
      return { ...line, km: round1(answer.km), kmSource: answer.source };
    });

    // Rounded once per truck, from the unrounded legs — adding up rounded rows
    // is a different sum, and the server rounds in exactly this order.
    return { ...truck, km: round1(km), lines };
  });

  return {
    ...report,
    trucks,
    distanceSource: allRoad ? "road" : "straight",
  };
}
